import { Component, OnInit, OnDestroy, ViewChild, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger, MatMenu } from "@angular/material/menu";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { SolverService } from "@app/services/rest/solver.service";
import { ClientConfigService } from "@app/core/config/client-config.service";
import {
  SolverWorkingInfo,
  UnplannableTicketEntry
} from "@app/models/dto/solver-issue";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { BookingNavigationService } from "@app/services/navigation/booking-navigation.service";
import { MatIconButton, MatButton } from "@angular/material/button";
import { NgClass } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { Subscription, firstValueFrom } from "rxjs";

@Component({
  selector: "app-held-tickets-tool",
  templateUrl: "./held-tickets-tool.component.html",
  styleUrls: ["./held-tickets-tool.component.scss"],
  imports: [
    MatIconButton,
    MatButton,
    MatMenuTrigger,
    NgClass,
    MatIcon,
    MatMenu,
    MatTooltip
  ]
})
export class HeldTicketsToolComponent implements OnInit, OnDestroy {
  solver = inject(SolverService);
  dialog = inject(MatDialog);
  router = inject(Router);
  http = inject(HttpClient);
  clientConfig = inject(ClientConfigService);
  private bookingNavigationService = inject(BookingNavigationService);

  @ViewChild("heldTrigger", { read: MatMenuTrigger, static: false })
  public heldTriggerBtn: MatMenuTrigger;

  public hasHeldTickets = false;
  public heldCount: number = 0;
  public heldEntries: UnplannableTicketEntry[] = [];
  private subscription: Subscription;

  ngOnInit() {
    this.subscription = this.solver.workingStatusStream.subscribe(
      (value: SolverWorkingInfo) => {
        if (value && value.unplannableTickets) {
          this.heldCount = value.unplannableTickets.length;
          this.hasHeldTickets = this.heldCount > 0;
        } else {
          this.heldCount = 0;
          this.hasHeldTickets = false;
        }
      }
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /** Fetch live placeability data from the REST endpoint when the menu opens. */
  async onMenuOpen() {
    try {
      const url = `${this.clientConfig.config.serviceUrl}/api/v1/solver/heldTickets`;
      this.heldEntries = await firstValueFrom(
        this.http.get<UnplannableTicketEntry[]>(url, { withCredentials: true })
      );
      this.heldCount = this.heldEntries.length;
      this.hasHeldTickets = this.heldCount > 0;
    } catch (e) {
      console.error("Failed to fetch held tickets:", e);
    }
  }

  openTicketDialog(ticketId: number) {
    this.dialog.open(TicketActionDialogComponent, {
      hasBackdrop: true,
      width: "500px",
      panelClass: "toolbar-popup-dialog",
      data: { ticketId }
    });
  }

  async referHeldVisit(entry: UnplannableTicketEntry) {
    try {
      const url = `${this.clientConfig.config.serviceUrl}/api/v1/solver/heldTickets/${entry.visitId}/refer`;
      await firstValueFrom(
        this.http.post(url, null, {
          responseType: "text",
          withCredentials: true
        })
      );
      console.log(`Referred held visit ${entry.visitId}`);
    } catch (e) {
      console.error(`Failed to refer held visit ${entry.visitId}:`, e);
    }
  }

  async reinsertHeldVisit(entry: UnplannableTicketEntry) {
    try {
      const url = `${this.clientConfig.config.serviceUrl}/api/v1/solver/heldTickets/${entry.visitId}/reinsert`;
      await firstValueFrom(
        this.http.post(url, null, {
          responseType: "text",
          withCredentials: true
        })
      );
      console.log(`Re-inserted held visit ${entry.visitId}`);
    } catch (e) {
      console.error(`Failed to re-insert held visit ${entry.visitId}:`, e);
    }
  }

  async unlockHeldVisit(entry: UnplannableTicketEntry) {
    try {
      const url = `${this.clientConfig.config.serviceUrl}/api/v1/solver/heldTickets/${entry.visitId}/unlock`;
      await firstValueFrom(
        this.http.post(url, null, {
          responseType: "text",
          withCredentials: true
        })
      );
      console.log(`Unlocked held visit ${entry.visitId}`);
      // Refresh placeability after unlock
      await this.onMenuOpen();
    } catch (e) {
      console.error(`Failed to unlock held visit ${entry.visitId}:`, e);
    }
  }

  openBookingPage(entry: UnplannableTicketEntry) {
    this.bookingNavigationService.setOriginalRoute(this.router.url);
    this.router.navigate(["/booking/visit/", entry.visitId]).then();
  }

  isVisitInProgress(entry: UnplannableTicketEntry): boolean {
    return entry.visitStatus === "IN_PROGRESS";
  }

  async convertToLoneWorker(entry: UnplannableTicketEntry) {
    try {
      const url = `${this.clientConfig.config.serviceUrl}/api/v1/solver/heldTickets/${entry.visitId}/convertToLoneWorker`;
      await firstValueFrom(
        this.http.post(url, null, {
          responseType: "text",
          withCredentials: true
        })
      );
      console.log(`Converted visit ${entry.visitId} to lone worker`);
      await this.onMenuOpen();
    } catch (e) {
      console.error(
        `Failed to convert visit ${entry.visitId} to lone worker:`,
        e
      );
    }
  }

  getTimeSinceExtraction(entry: UnplannableTicketEntry): string {
    const now = Math.floor(Date.now() / 1000);
    const seconds = now - entry.extractedAt;
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }
}
